import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CompanionProvider, useCompanion } from "./context/CompanionContext";
import { AnimatePresence } from "framer-motion";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import { DesktopGate } from "./components/DesktopGate";
import { Onboarding } from "./components/onboarding";
import "./styles/App.scss";

// Pages
const Home = React.lazy(() => import("./pages/Home"));
const ObjectDetail = React.lazy(() => import("./pages/ObjectDetail"));
const Playground = React.lazy(() => import("./pages/Playground"));

// Admin pages
const AdminPinGate = React.lazy(() => import("./pages/admin/AdminPinGate"));
const AdminHome = React.lazy(() => import("./pages/admin/AdminHome"));
const AdminUsage = React.lazy(() => import("./pages/admin/AdminUsage"));
const ObjectSetup = React.lazy(() => import("./pages/admin/ObjectSetup"));
const ShrineSetup = React.lazy(() => import("./pages/admin/ShrineSetup"));

// Loading component
function LoadingSpinner() {
  return (
    <div className="loading-container">
      <div className="loading-spinner" />
      <p>Loading...</p>
    </div>
  );
}

// Error boundary for lazy-loaded components
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>Please refresh the page and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Animated routes wrapper
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence initial={false}>
      <React.Suspense fallback={<LoadingSpinner />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/object/:identifier" element={<ObjectDetail />} />
          {/* Dev-only playground route */}
          {import.meta.env.DEV && (
            <Route path="/playground" element={<Playground />} />
          )}
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminPinGate />} />
          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute>
                <AdminHome />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/usage"
            element={
              <ProtectedAdminRoute>
                <AdminUsage />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/object/:objectType"
            element={
              <ProtectedAdminRoute>
                <ObjectSetup />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/shrine/:shrineType"
            element={
              <ProtectedAdminRoute>
                <ShrineSetup />
              </ProtectedAdminRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
    </AnimatePresence>
  );
}

// Onboarding gate - shows onboarding for first-time users
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const {
    isFirstVisit,
    hasCompletedOnboarding,
    completeOnboarding,
    setAddObjectFlow,
  } = useCompanion();
  const location = useLocation();

  // Skip onboarding for admin routes
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Show onboarding if first visit and not completed, unless on admin route
  const shouldShowOnboarding =
    isFirstVisit && !hasCompletedOnboarding && !isAdminRoute;

  const handleOnboardingComplete = (openAddObject?: boolean) => {
    completeOnboarding();
    // Open the add object modal if requested (user didn't skip)
    if (openAddObject) {
      // Small delay to let the onboarding unmount smoothly
      setTimeout(() => {
        setAddObjectFlow({ step: "select" });
      }, 100);
    }
  };

  if (shouldShowOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  return <>{children}</>;
}

// Main App component
function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Prevent zoom on double tap (iOS Safari)
  useEffect(() => {
    let lastTouchEnd = 0;
    const preventZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("touchend", preventZoom, { passive: false });
    return () => document.removeEventListener("touchend", preventZoom);
  }, []);

  return (
    <ErrorBoundary>
      <DesktopGate>
        <BrowserRouter>
          <CompanionProvider>
            <OnboardingGate>
              <div className="app">
                {/* Offline indicator */}
                {!isOnline && (
                  <div className="offline-indicator">
                    <span>OFFLINE - Some features may be limited</span>
                  </div>
                )}

                {/* Main content */}
                <main className="main-content">
                  <AnimatedRoutes />
                </main>

                {/* Safe area spacer for iOS */}
                <div className="safe-area-spacer" />
              </div>
            </OnboardingGate>
          </CompanionProvider>
        </BrowserRouter>
      </DesktopGate>
    </ErrorBoundary>
  );
}

export default App;
